import apiClient from "../../helpers/axios";

/*
 * Раздел «Задачи»: списки с участниками, задачи, подзадачи, комментарии,
 * файлы и голосовые с расшифровкой, календарь, связь с чатом.
 * BACKEND: controller/todoLists.js, маршруты в routes/todoLists.js.
 */

/**
 * Часовой пояс компьютера (минуты). Сервер считает по нему повтор «каждый
 * месяц»: какое сегодня число, зависит от того, где находится человек.
 */
const tzOffsetMin = () => new Date().getTimezoneOffset();

const listOf = (value) => (Array.isArray(value) ? value : []);

// ---------- списки ----------

export const fetchLists = async () => {
    const res = await apiClient.get("/todo-lists");
    return listOf(res.data?.lists);
};

export const createList = async ({ title, memberIds = [] }) => {
    const res = await apiClient.post("/todo-lists", { title, memberIds });
    return res.data.list;
};

export const updateList = async (listId, payload) => {
    const res = await apiClient.patch(`/todo-lists/${listId}`, payload);
    return res.data.list;
};

/** Закрепить или скрыть список у себя. У остальных участников он не меняется. */
export const setListState = async (listId, payload) => {
    const res = await apiClient.put(`/todo-lists/${listId}/state`, payload);
    return res.data.list;
};

export const deleteList = async (listId) => {
    await apiClient.delete(`/todo-lists/${listId}`);
};

export const leaveList = async (listId) => {
    await apiClient.post(`/todo-lists/${listId}/leave`);
};

// ---------- задачи ----------

/** Задачи списка вместе с подзадачами (у подзадачи есть parentId). */
export const fetchTasks = async (listId) => {
    const res = await apiClient.get(`/todo-lists/${listId}/tasks`);
    return { list: res.data?.list || null, tasks: listOf(res.data?.tasks) };
};

/**
 * Новая задача. draft: title, description, parentId (подзадача), assigneeId,
 * priority, dueAt (ISO), remindBeforeMin, repeat.
 */
export const createTask = async (listId, draft = {}) => {
    const body = {};
    if (draft.title) body.title = draft.title;
    if (draft.description) body.description = draft.description;
    if (draft.parentId) body.parentId = draft.parentId;
    if (draft.assigneeId) body.assigneeId = draft.assigneeId;
    if (draft.priority) body.priority = draft.priority;

    if (draft.dueAt) {
        body.dueAt = draft.dueAt;
        body.tzOffsetMin = tzOffsetMin();
        if (draft.remindBeforeMin !== undefined) body.remindBeforeMin = draft.remindBeforeMin;
        if (draft.repeat) body.repeat = draft.repeat;
    }

    const res = await apiClient.post(`/todo-lists/${listId}/tasks`, body);
    return res.data.task;
};

export const fetchTask = async (taskId) => {
    const res = await apiClient.get(`/todo-tasks/${taskId}`);
    return { task: res.data.task, subtasks: listOf(res.data?.subtasks) };
};

/**
 * Изменить задачу. patch: title, description, status, priority, pinned,
 * assigneeId (пустая строка — без исполнителя), dueAt (null — убрать срок),
 * remindBeforeMin, repeat.
 */
export const updateTask = async (taskId, patch = {}) => {
    // Срок или повтор меняются — сервер должен знать часовой пояс.
    const body = patch.dueAt !== undefined || patch.repeat !== undefined
        ? { ...patch, tzOffsetMin: tzOffsetMin() }
        : patch;

    const res = await apiClient.patch(`/todo-tasks/${taskId}`, body);
    return res.data.task;
};

/** Удалить задачу. Возвращает id удалённых задач (сама и её подзадачи). */
export const deleteTask = async (taskId) => {
    const res = await apiClient.delete(`/todo-tasks/${taskId}`);
    return listOf(res.data?.taskIds).length ? res.data.taskIds.map(String) : [String(taskId)];
};

// ---------- комментарии и файлы ----------

/** attachmentId — комментарий относится к этому вложению задачи. */
export const addComment = async (taskId, text, attachmentId = "") => {
    const res = await apiClient.post(`/todo-tasks/${taskId}/comments`, {
        text,
        ...(attachmentId ? { attachmentId } : {}),
    });
    return res.data.task;
};

export const deleteComment = async (taskId, commentId) => {
    const res = await apiClient.delete(`/todo-tasks/${taskId}/comments/${commentId}`);
    return res.data.task;
};

/** Приложить файл к задаче. onProgress получает проценты отправки. */
export const addAttachment = async (taskId, file, onProgress) => {
    const form = new FormData();
    // Настоящее имя файла идёт отдельным полем: в заголовках кириллица портится.
    form.append("originalName", file.name || "file");
    form.append("file", file, file.name || "file");

    const res = await apiClient.post(`/todo-tasks/${taskId}/attachments`, form, {
        // Большой файл грузится долго — не обрываем сами.
        timeout: 0,
        onUploadProgress: (event) => {
            if (!onProgress || !event?.total) return;
            onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
        },
    });
    return res.data.task;
};

export const deleteAttachment = async (taskId, attachmentId) => {
    const res = await apiClient.delete(`/todo-tasks/${taskId}/attachments/${attachmentId}`);
    return res.data.task;
};

/** Расшифровать голосовое заново. */
export const retryTranscript = async (taskId, attachmentId) => {
    const res = await apiClient.post(`/todo-tasks/${taskId}/attachments/${attachmentId}/transcribe`);
    return res.data.task;
};

/** Файл задачи закрытый: получаем его запросом с токеном. */
export const fetchAttachmentBlob = async (taskId, attachmentId, { download = false } = {}) => {
    const res = await apiClient.get(`/todo-tasks/${taskId}/attachments/${attachmentId}`, {
        responseType: "blob",
        timeout: 0,
        ...(download ? { params: { download: 1 } } : {}),
    });
    return res.data;
};

// ---------- календарь ----------

/**
 * Задачи со сроком за период и будущие повторы. Без listId — из всех моих
 * списков, с listId — только из этого списка (календарь списка).
 */
export const fetchCalendar = async (from, to, listId = "") => {
    const res = await apiClient.get("/todo-tasks/calendar", {
        params: { from: from.toISOString(), to: to.toISOString(), ...(listId ? { listId } : {}) },
    });
    return {
        lists: listOf(res.data?.lists),
        tasks: listOf(res.data?.tasks),
        repeats: listOf(res.data?.repeats),
    };
};

// ---------- задачи и чат ----------

/** Отправить задачу в чат карточкой. Участники чата смогут её открыть, но не менять. */
export const shareTask = async (taskId, chatId) => {
    const res = await apiClient.post(`/todo-tasks/${taskId}/share`, { chatId });
    return res.data.task;
};

/** Отправить задачу в чат обычным текстом. */
export const sendTextToChat = async (chatId, text) => {
    await apiClient.post(`/messages/${chatId}`, { text });
};

/** Задача по карточке из чата (только просмотр). */
export const fetchSharedTask = async (taskId) => {
    const res = await apiClient.get(`/todo-tasks/${taskId}/shared`);
    return {
        task: res.data.task,
        subtasks: listOf(res.data?.subtasks),
        listTitle: String(res.data?.listTitle || ""),
        canEdit: res.data?.canEdit === true,
    };
};

/** Сообщения чата → новая задача: текст — название и описание, файлы — вложения. */
export const createTaskFromMessages = async (listId, messageIds) => {
    const res = await apiClient.post(`/todo-lists/${listId}/tasks/from-message`, { messageIds });
    return res.data.task;
};

/** Сообщения чата → в существующую задачу: файлы — вложениями, текст — комментарием. */
export const addFromMessages = async (taskId, messageIds) => {
    const res = await apiClient.post(`/todo-tasks/${taskId}/from-message`, { messageIds });
    return {
        task: res.data.task,
        files: Number(res.data?.added?.files || 0),
        comments: Number(res.data?.added?.comments || 0),
    };
};
